import { useCallback, useEffect, useRef, useState } from 'react';
import { updateServiceRequestStatus } from '../api/updateServiceRequestStatusApiService';
import {
  UPDATE_SR_CANCELLATION_REASONS,
  UPDATE_SR_STATUS_LABELS as L,
  type UpdateSrCancellationReasonId,
} from '../constants/labels';
import { UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES } from '../errors';
import { getAllowedNextStatuses } from '../pages/viewServiceRequest/viewServiceRequestHelpers';
import { isCancellableNow } from '../utils/cancellationPolicy';
import type { ServiceRequestStatus } from '../types/serviceRequestListType';

export type UpdateSrStep = 'form' | 'cancel-reason';

interface UseUpdateServiceRequestStatusOptions {
  serviceRequestId: string | undefined;
  currentStatus: ServiceRequestStatus | undefined;
  /**
   * Scheduled start (UTC ISO). Used to enforce the FE 24-hour cancel gate —
   * when the SR is `assigned` and the visit is less than 24h away, the
   * `cancelled` option drops out of the allowed-status list and the
   * cancel CTA is suppressed.
   */
  scheduledStartAt?: string | null | undefined;
  /** Receives the status that was just submitted, so the caller can pick a status-specific success message (e.g. "cancelled" vs generic). */
  onSuccess?: (submittedStatus: ServiceRequestStatus) => void;
}

export interface UseUpdateServiceRequestStatusReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;

  // Step
  step: UpdateSrStep;
  goToCancelReasonStep: () => void;
  goBackToForm: () => void;

  newStatus: ServiceRequestStatus | '';
  setNewStatus: (status: ServiceRequestStatus) => void;
  adminNotes: string;
  setAdminNotes: (value: string) => void;
  notify: boolean;
  setNotify: (value: boolean) => void;
  newStatusError: string;
  adminNotesError: string;

  // Cancel-reason step
  cancellationReason: UpdateSrCancellationReasonId | '';
  setCancellationReason: (id: UpdateSrCancellationReasonId) => void;
  customReason: string;
  setCustomReason: (value: string) => void;
  cancellationReasonError: string;
  customReasonError: string;

  isSubmitting: boolean;
  submitError: string;
  clearSubmitError: () => void;
  allowedStatuses: ServiceRequestStatus[];
  /** True when the current step has a valid payload that the user can submit. */
  canSubmit: boolean;
  /** True when step 1 has been completed enough to advance to the cancel-reason step. */
  canContinue: boolean;
  submit: () => Promise<void>;
}

export const useUpdateServiceRequestStatus = ({
  serviceRequestId,
  currentStatus,
  scheduledStartAt,
  onSuccess,
}: UseUpdateServiceRequestStatusOptions): UseUpdateServiceRequestStatusReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<UpdateSrStep>('form');
  const [newStatus, setNewStatusState] = useState<ServiceRequestStatus | ''>(
    ''
  );
  const [adminNotes, setAdminNotesState] = useState('');
  const [notify, setNotify] = useState(false);
  const [newStatusError, setNewStatusError] = useState('');
  const [adminNotesError, setAdminNotesError] = useState('');

  const [cancellationReason, setCancellationReasonState] = useState<
    UpdateSrCancellationReasonId | ''
  >('');
  const [customReason, setCustomReasonState] = useState('');
  const [cancellationReasonError, setCancellationReasonError] = useState('');
  const [customReasonError, setCustomReasonError] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [submitError, setSubmitError] = useState('');

  const allTransitions = currentStatus
    ? getAllowedNextStatuses(currentStatus)
    : [];
  // Hide 'cancelled' from the dropdown when the FE 24h cancel gate is closed
  // (e.g. assigned SR with <24h to visit). BE remains the authoritative gate.
  const cancelEligibility = isCancellableNow(currentStatus, scheduledStartAt);
  const allowedStatuses = cancelEligibility.canCancel
    ? allTransitions
    : allTransitions.filter((s) => s !== 'cancelled');

  const resetForm = useCallback(() => {
    setStep('form');
    setNewStatusState('');
    setAdminNotesState('');
    setNotify(false);
    setNewStatusError('');
    setAdminNotesError('');
    setCancellationReasonState('');
    setCustomReasonState('');
    setCancellationReasonError('');
    setCustomReasonError('');
    setSubmitError('');
  }, []);

  const open = useCallback(() => {
    resetForm();
    setIsOpen(true);
  }, [resetForm]);

  const close = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsOpen(false);
    resetForm();
  }, [isSubmitting, resetForm]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    resetForm();
  }, [serviceRequestId, currentStatus, isOpen, resetForm]);

  const setNewStatus = useCallback((status: ServiceRequestStatus) => {
    setNewStatusState(status);
    setNewStatusError('');
    // Switching off 'cancelled' resets any cancel-step state so the user
    // doesn't accidentally carry over a stale reason.
    if (status !== 'cancelled') {
      setCancellationReasonState('');
      setCustomReasonState('');
      setCancellationReasonError('');
      setCustomReasonError('');
    } else {
      // Admin notes don't apply when cancelling — clear the field so the
      // submit payload is clean if the user toggled back and forth.
      setAdminNotesState('');
      setAdminNotesError('');
    }
  }, []);

  const setAdminNotes = useCallback((value: string) => {
    setAdminNotesState(value);
    if (value.trim().length <= L.NOTES_MAX_LENGTH) {
      setAdminNotesError('');
    }
  }, []);

  const setCancellationReason = useCallback(
    (id: UpdateSrCancellationReasonId) => {
      setCancellationReasonState(id);
      setCancellationReasonError('');
      // Picking a non-Other option clears the custom text so it doesn't
      // get submitted by accident.
      if (id !== 'other') {
        setCustomReasonState('');
        setCustomReasonError('');
      }
    },
    []
  );

  const setCustomReason = useCallback((value: string) => {
    setCustomReasonState(value);
    if (value.trim().length <= L.NOTES_MAX_LENGTH) {
      setCustomReasonError('');
    }
  }, []);

  const clearSubmitError = useCallback(() => setSubmitError(''), []);

  const isCancelling = newStatus === 'cancelled';

  // Step 1's Continue / Update Status button stays enabled so the user can
  // click it and surface the required-field error on the status dropdown.
  // Only the in-flight state disables it.
  const canContinue = !isSubmitting;

  // Cancel-step Confirm button stays enabled so the user can click it and
  // surface required-field errors. Only the in-flight state disables it.
  const cancelStepCanSubmit = !isSubmitting;

  const canSubmit =
    step === 'cancel-reason'
      ? cancelStepCanSubmit
      : isCancelling
        ? false
        : !isSubmitting;

  const goToCancelReasonStep = useCallback(() => {
    if (!newStatus) {
      setNewStatusError(L.ERROR_REQUIRED);
      return;
    }
    if (newStatus !== 'cancelled') {
      return;
    }
    setStep('cancel-reason');
    setSubmitError('');
  }, [newStatus]);

  const goBackToForm = useCallback(() => {
    setStep('form');
    setSubmitError('');
  }, []);

  const submit = useCallback(async () => {
    if (!serviceRequestId || isSubmittingRef.current) {
      return;
    }
    // Step-aware validation. Both branches return early on failure so the
    // common API call below only runs once everything is valid.
    let hasError = false;
    if (step === 'cancel-reason') {
      if (!cancellationReason) {
        setCancellationReasonError(L.ERROR_REQUIRED);
        hasError = true;
      }
      if (cancellationReason === 'other' && customReason.trim().length === 0) {
        setCustomReasonError(L.ERROR_REQUIRED);
        hasError = true;
      }
      if (customReason.trim().length > L.NOTES_MAX_LENGTH) {
        setCustomReasonError(L.ERROR_REASON_TOO_LONG);
        hasError = true;
      }
    } else {
      if (!newStatus) {
        setNewStatusError(L.ERROR_REQUIRED);
        hasError = true;
      }
      if (adminNotes.trim().length > L.NOTES_MAX_LENGTH) {
        setAdminNotesError(L.ERROR_NOTES_TOO_LONG);
        hasError = true;
      }
    }
    if (hasError) {
      return;
    }
    // On the cancel-reason step `newStatus` is already 'cancelled' (set on
    // step 1) and `adminNotes` was cleared when the user picked cancel.
    // The cancellation reason itself lives in local state only — once the
    // BE adds a `cancellation_reason` field, send it here.

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError('');

    // For cancellation the BE requires `admin_notes` to carry the
    // cancellation reason (see services/.../service_request.types.ts —
    // superRefine on new_status === CANCELLED). Resolve it from the
    // cancel-step state: free-text when reason === 'other', otherwise the
    // label of the picked preset.
    let payloadAdminNotes = adminNotes;
    if (newStatus === 'cancelled') {
      if (cancellationReason === 'other') {
        payloadAdminNotes = customReason.trim();
      } else if (cancellationReason) {
        const preset = UPDATE_SR_CANCELLATION_REASONS.find(
          (r) => r.id === cancellationReason
        );
        payloadAdminNotes = preset?.label ?? '';
      }
    }

    try {
      const result = await updateServiceRequestStatus({
        serviceRequestId,
        newStatus: newStatus as ServiceRequestStatus,
        adminNotes: payloadAdminNotes,
        notify,
      });
      if (result.success) {
        const submittedStatus = newStatus as ServiceRequestStatus;
        setIsOpen(false);
        resetForm();
        onSuccess?.(submittedStatus);
      } else {
        setSubmitError(
          result.error ??
            UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.DEFAULT_ERROR
        );
      }
    } catch {
      setSubmitError(
        UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.UNEXPECTED_ERROR
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    serviceRequestId,
    step,
    newStatus,
    adminNotes,
    notify,
    cancellationReason,
    customReason,
    onSuccess,
    resetForm,
  ]);

  return {
    isOpen,
    open,
    close,
    step,
    goToCancelReasonStep,
    goBackToForm,
    newStatus,
    setNewStatus,
    adminNotes,
    setAdminNotes,
    notify,
    setNotify,
    newStatusError,
    adminNotesError,
    cancellationReason,
    setCancellationReason,
    customReason,
    setCustomReason,
    cancellationReasonError,
    customReasonError,
    isSubmitting,
    submitError,
    clearSubmitError,
    allowedStatuses,
    canSubmit,
    canContinue,
    submit,
  };
};
